import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_ORIGIN } from '../../config';

/** The signed-in person, as Better Auth returns it. */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  image?: string | null;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  // Same-origin (the dev proxy / Railway), but `include` makes the cookie
  // travel explicitly rather than relying on the browser's same-origin default.
  baseQuery: fetchBaseQuery({ baseUrl: `${API_ORIGIN}/api/auth`, credentials: 'include' }),
  tagTypes: ['Session'],
  endpoints: (b) => ({
    getSession: b.query<SessionUser | null, void>({
      query: () => '/get-session',
      transformResponse: (raw: { user?: SessionUser | null } | null) => raw?.user ?? null,
      providesTags: ['Session'],
    }),
    signIn: b.mutation<SessionUser, { email: string; password: string }>({
      query: (body) => ({ url: '/sign-in/email', method: 'POST', body }),
      transformResponse: (raw: { user: SessionUser }) => raw.user,
      invalidatesTags: ['Session'],
    }),
    signUp: b.mutation<SessionUser, { email: string; password: string; name: string }>({
      query: (body) => ({ url: '/sign-up/email', method: 'POST', body }),
      transformResponse: (raw: { user: SessionUser }) => raw.user,
      invalidatesTags: ['Session'],
    }),
    signOut: b.mutation<{ ok: boolean }, void>({
      query: () => ({ url: '/sign-out', method: 'POST' }),
      invalidatesTags: ['Session'],
    }),
    /**
     * Email a password-reset link. Better Auth always answers the same way
     * whether or not the address exists, so this cannot be used to probe the
     * user table.
     */
    requestPasswordReset: b.mutation<
      { status: boolean; message?: string },
      { email: string; redirectTo?: string }
    >({
      query: (body) => ({ url: '/request-password-reset', method: 'POST', body }),
    }),
    /** Send the "confirm your email" link again (the server sets where it lands). */
    sendVerificationEmail: b.mutation<{ status: boolean }, { email: string }>({
      query: ({ email }) => ({ url: '/send-verification-email', method: 'POST', body: { email } }),
    }),
    /** Finish the reset: token came from the email link, newPassword is fresh. */
    resetPassword: b.mutation<{ status: boolean }, { newPassword: string; token: string }>({
      query: (body) => ({ url: '/reset-password', method: 'POST', body }),
    }),
    /** Signed-in password change (profile screen): current password required. */
    changePassword: b.mutation<
      { status: boolean },
      { currentPassword: string; newPassword: string }
    >({
      query: (body) => ({ url: '/change-password', method: 'POST', body }),
      invalidatesTags: ['Session'],
    }),
  }),
});

export const {
  useGetSessionQuery,
  useSignInMutation,
  useSignUpMutation,
  useSignOutMutation,
  useRequestPasswordResetMutation,
  useSendVerificationEmailMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
} = authApi;